import type { Emulator } from "../emulator.js";
import { createWorkbenchMenuBar } from "../workbench-menu-bar.js";
import type { WorkbenchMenu, WorkbenchMenuBarController } from "../workbench-menu-types.js";
import { actionMenuItem, buttonMenuItem, checkboxMenuItem, menuSeparator } from "../workbench-menu-utils.js";
import {
  allNoteOptions,
  clampKeyboardOctave,
  isNoteValidForPitchMode,
  makeNoteCode,
  noteCodeToLabel,
  pitchModeDisplayName,
  pitchModeRangeLabel
} from "./note-data.js";
import { analyzeTrackerProject, compileTrackerProject, type TrackerCompileMetricSeverity } from "./compiler.js";
import { readProjectFile, downloadProjectJson, downloadTrackerBytecode, loadDraftProject, saveDraftProject } from "./export.js";
import { formatCellAriaLabel, formatCellParts, formatInstrumentRef, formatOrderList, parseOrderList, TRACKER_EFFECT_OPTIONS } from "./format.js";
import { handleTrackerKeyDown, handleTrackerKeyUp } from "./keyboard.js";
import {
  effectArgHint,
  effectHelp,
  effectName,
  effectScopeLabel,
  isTrackerControlNote,
  sanitizeEffectArg,
  TRACKER_EFFECT,
  TRACKER_INSTRUMENT_EMPTY,
  TRACKER_NOTE_EMPTY,
  TRACKER_PITCH_MODE,
  TRACKER_WAVEFORM,
  type ChannelEvent,
  type TrackerProject
} from "./model.js";
import { TRACKER_STATUS_STATE } from "./emulator-status.js";
import { SoundTrackerPlayback, type SoundTrackerPlaybackHost } from "./playback.js";
import { TrackerStore, trackerSelectionBounds } from "./store.js";

export type SoundTrackerController = {
  refresh(): void;
  setVisible(visible: boolean): void;
  syncFromEmulator(): void;
};

type GridCell = {
  button: HTMLButtonElement;
  channel: number;
  color: HTMLSpanElement;
  effect: HTMLSpanElement;
  effectArg: HTMLSpanElement;
  instrument: HTMLSpanElement;
  note: HTMLSpanElement;
  row: number;
};

type GridRow = {
  element: HTMLTableRowElement;
  label: HTMLTableCellElement;
  row: number;
};

type OrderChip = {
  button: HTMLButtonElement;
  pattern: HTMLSpanElement;
  slot: HTMLSpanElement;
};

type PatternOptionItem = {
  button: HTMLButtonElement;
};

type InstrumentListItem = {
  button: HTMLButtonElement;
};

type TrackerDiagnosticsResult = {
  diagnostics: ReturnType<typeof analyzeTrackerProject> | null;
  error: string | null;
};

type ActivePlaybackButton = "song" | "pattern" | "row" | "instrument" | null;
type ActiveTransportButton = Exclude<ActivePlaybackButton, "instrument" | null>;

function expectElement<T extends HTMLElement>(root: ParentNode, id: string, ctor: { new (): T }): T {
  const element = root.querySelector(`#${id}`);
  if (!(element instanceof ctor)) {
    throw new Error(`missing tracker element #${id}`);
  }
  return element;
}

function playbackStateText(state: ReturnType<TrackerStore["getState"]>["ui"]["playback"]["state"]): string {
  switch (state) {
    case "written":
      return "Written to emulator workspace.";
    case "playing":
      return "Guest player is running in the main emulator.";
    case "stopped":
      return "Guest player stopped and left the workspace intact.";
    case "error":
      return "Tracker playback hit an error.";
    case "auditioning":
      return "Instrument audition is using the main emulator.";
    default:
      return "Tracker is editing the browser project model.";
  }
}

function trackerFilterMode(filterCtrl: number): number {
  const mode = (filterCtrl >> 4) & 0x03;
  return mode <= 0x02 ? mode : 0;
}

function composeTrackerFilterCtrl(resonance: number, mode: number): number {
  const res = Math.max(0, Math.min(0x0f, Math.round(resonance))) & 0x0f;
  const selectedMode = Math.max(0, Math.min(0x02, Math.round(mode))) & 0x03;
  return res | (selectedMode << 4);
}

function setInputValue(input: HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement, value: string): void {
  if (input.value !== value) {
    input.value = value;
  }
}

function setInputValueUnlessFocused(input: HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement, value: string): void {
  if (document.activeElement === input) {
    return;
  }
  setInputValue(input, value);
}

function updateSelectOptions(select: HTMLSelectElement, options: Array<{ value: string; label: string }>): void {
  const signature = options.map((option) => `${option.value}:${option.label}`).join("|");
  if (select.dataset.signature === signature) {
    return;
  }
  select.textContent = "";
  for (const option of options) {
    const node = document.createElement("option");
    node.value = option.value;
    node.textContent = option.label;
    select.append(node);
  }
  select.dataset.signature = signature;
}

function selectedCellSummary(event: ChannelEvent): string {
  return `${noteCodeToLabel(event.note)} ${formatInstrumentRef(event.instrument)} ${effectName(event.effect)} ${event.effectArg
    .toString(16)
    .toUpperCase()
    .padStart(2, "0")}`;
}

function trackerCellKey(pattern: number, row: number, channel: number): string {
  return `${pattern}:${row}:${channel}`;
}

function setText(node: HTMLElement, value: string): void {
  if (node.textContent !== value) {
    node.textContent = value;
  }
}

function metricTone(metric: TrackerCompileMetricSeverity): string {
  switch (metric) {
    case "error":
      return "error";
    case "warn":
      return "warn";
    default:
      return "ok";
  }
}

function latestResultTone(state: ReturnType<TrackerStore["getState"]>): "ok" | "error" {
  return state.ui.playback.lastError === null ? "ok" : "error";
}

function channelEffectivelyMuted(state: ReturnType<TrackerStore["getState"]>, channel: number): boolean {
  const mask = 1 << channel;
  if ((state.ui.muteMask & mask) !== 0) {
    return true;
  }
  return state.ui.soloMask !== 0 && (state.ui.soloMask & mask) === 0;
}

export function setupSoundTracker(
  emulator: Emulator,
  panel: HTMLElement,
  host: SoundTrackerPlaybackHost
): SoundTrackerController {
  const menuHost = expectElement(panel, "tracker-menu-bar", HTMLDivElement);
  const trackerLayout = expectElement(panel, "tracker-layout", HTMLDivElement);
  const leftRail = expectElement(panel, "tracker-left-rail", HTMLButtonElement);
  const rightRail = expectElement(panel, "tracker-right-rail", HTMLButtonElement);
  const grid = expectElement(panel, "tracker-grid", HTMLDivElement);
  const gridBody = expectElement(panel, "tracker-grid-body", HTMLTableSectionElement);
  const gridScroll = grid.querySelector(".tracker-grid__scroll");
  if (!(gridScroll instanceof HTMLDivElement)) {
    throw new Error("missing tracker grid scroll container");
  }
  const songTitle = expectElement(panel, "tracker-song-title", HTMLInputElement);
  const speed = expectElement(panel, "tracker-speed", HTMLInputElement);
  const tempo = expectElement(panel, "tracker-tempo", HTMLInputElement);
  const orderList = expectElement(panel, "tracker-order-list", HTMLInputElement);
  const orderStrip = expectElement(panel, "tracker-order-strip", HTMLDivElement);
  const activePattern = expectElement(panel, "tracker-active-pattern", HTMLSelectElement);
  const newSong = expectElement(panel, "tracker-new-song", HTMLButtonElement);
  const addPattern = expectElement(panel, "tracker-add-pattern", HTMLButtonElement);
  const deletePattern = expectElement(panel, "tracker-delete-pattern", HTMLButtonElement);
  const duplicatePattern = expectElement(panel, "tracker-duplicate-pattern", HTMLButtonElement);
  const playSong = expectElement(panel, "tracker-play-song", HTMLButtonElement);
  const playPattern = expectElement(panel, "tracker-play-pattern", HTMLButtonElement);
  const playFromRow = expectElement(panel, "tracker-play-row", HTMLButtonElement);
  const loopToggle = expectElement(panel, "tracker-loop", HTMLInputElement);
  const followToggle = expectElement(panel, "tracker-follow", HTMLInputElement);
  const editStep = expectElement(panel, "tracker-edit-step", HTMLSelectElement);
  const octave = expectElement(panel, "tracker-octave", HTMLSelectElement);
  const patternName = expectElement(panel, "tracker-pattern-name", HTMLInputElement);
  const patternSelector = expectElement(panel, "tracker-pattern-selector", HTMLDivElement);
  const patternNameToggle = expectElement(panel, "tracker-pattern-name-toggle", HTMLButtonElement);
  const patternNameOptions = expectElement(panel, "tracker-pattern-name-options", HTMLDivElement);
  const cellNote = expectElement(panel, "tracker-cell-note", HTMLSelectElement);
  const cellInstrument = expectElement(panel, "tracker-cell-instrument", HTMLSelectElement);
  const cellEffect = expectElement(panel, "tracker-cell-effect", HTMLSelectElement);
  const cellEffectArg = expectElement(panel, "tracker-cell-effect-arg", HTMLInputElement);
  const effectCode = expectElement(panel, "tracker-effect-code", HTMLParagraphElement);
  const effectScope = expectElement(panel, "tracker-effect-scope", HTMLSpanElement);
  const effectDescription = expectElement(panel, "tracker-effect-description", HTMLParagraphElement);
  const effectArgHelp = expectElement(panel, "tracker-effect-arg-hint", HTMLParagraphElement);
  const copy = expectElement(panel, "tracker-copy", HTMLButtonElement);
  const paste = expectElement(panel, "tracker-paste", HTMLButtonElement);
  const clear = expectElement(panel, "tracker-clear", HTMLButtonElement);
  const transposeUp = expectElement(panel, "tracker-transpose-up", HTMLButtonElement);
  const transposeDown = expectElement(panel, "tracker-transpose-down", HTMLButtonElement);
  const insertRow = expectElement(panel, "tracker-insert-row", HTMLButtonElement);
  const deleteRow = expectElement(panel, "tracker-delete-row", HTMLButtonElement);
  const duplicateRow = expectElement(panel, "tracker-duplicate-row", HTMLButtonElement);
  const undo = expectElement(panel, "tracker-undo", HTMLButtonElement);
  const redo = expectElement(panel, "tracker-redo", HTMLButtonElement);
  const historyStatus = expectElement(panel, "tracker-history-status", HTMLParagraphElement);
  const instrumentList = expectElement(panel, "tracker-instrument-list", HTMLDivElement);
  const instrumentName = expectElement(panel, "tracker-instrument-name", HTMLInputElement);
  const addInstrument = expectElement(panel, "tracker-instrument-add", HTMLButtonElement);
  const deleteInstrument = expectElement(panel, "tracker-instrument-delete", HTMLButtonElement);
  const duplicateInstrument = expectElement(panel, "tracker-instrument-duplicate", HTMLButtonElement);
  const playInstrument = expectElement(panel, "tracker-instrument-play", HTMLButtonElement);
  const waveform = expectElement(panel, "tracker-waveform", HTMLSelectElement);
  const sync = expectElement(panel, "tracker-sync", HTMLInputElement);
  const ring = expectElement(panel, "tracker-ring", HTMLInputElement);
  const test = expectElement(panel, "tracker-test", HTMLInputElement);
  const attack = expectElement(panel, "tracker-attack", HTMLSelectElement);
  const decay = expectElement(panel, "tracker-decay", HTMLSelectElement);
  const sustain = expectElement(panel, "tracker-sustain", HTMLSelectElement);
  const release = expectElement(panel, "tracker-release", HTMLSelectElement);
  const pulseWidthField = expectElement(panel, "tracker-pulse-width-field", HTMLDivElement);
  const pulseWidth = expectElement(panel, "tracker-pulse-width", HTMLInputElement);
  const rateX8 = expectElement(panel, "tracker-rate-x8", HTMLInputElement);
  const filterRoute = expectElement(panel, "tracker-filter-route", HTMLInputElement);
  const globalPitch = expectElement(panel, "tracker-global-pitch", HTMLSelectElement);
  const pitchRangeHint = expectElement(panel, "tracker-pitch-range-hint", HTMLParagraphElement);
  const globalVolume = expectElement(panel, "tracker-global-volume", HTMLInputElement);
  const globalCutoff = expectElement(panel, "tracker-global-cutoff", HTMLInputElement);
  const globalFilterRes = expectElement(panel, "tracker-global-filter-res", HTMLSelectElement);
  const globalFilterMode = expectElement(panel, "tracker-global-filter-mode", HTMLSelectElement);
  const selectionStatus = expectElement(panel, "tracker-selection-status", HTMLParagraphElement);
  const ownershipStatus = expectElement(panel, "tracker-ownership-status", HTMLParagraphElement);
  const audioDiagnostics = expectElement(panel, "tracker-audio-diagnostics", HTMLParagraphElement);
  const audioStatus = expectElement(panel, "tracker-audio-status", HTMLParagraphElement);
  const playbackStatus = expectElement(panel, "tracker-playback-status", HTMLParagraphElement);
  const errorStatus = expectElement(panel, "tracker-error-status", HTMLParagraphElement);
  const diagnosticsList = expectElement(panel, "tracker-diagnostics-list", HTMLDivElement);
  const diagnosticsIssues = expectElement(panel, "tracker-diagnostics-issues", HTMLDivElement);
  const diagnosticsSummary = expectElement(panel, "tracker-diagnostics-summary", HTMLParagraphElement);
  const exportJson = expectElement(panel, "tracker-export-json", HTMLButtonElement);
  const exportBytecode = expectElement(panel, "tracker-export-bytecode", HTMLButtonElement);
  const importJson = expectElement(panel, "tracker-import-json", HTMLButtonElement);
  const importJsonFile = expectElement(panel, "tracker-import-json-file", HTMLInputElement);
  const writeEmulator = expectElement(panel, "tracker-write-emulator", HTMLButtonElement);
  const startEmulator = expectElement(panel, "tracker-start-emulator", HTMLButtonElement);

  const store = new TrackerStore(loadDraftProject() ?? undefined);
  const playback = new SoundTrackerPlayback(emulator, host);
  const gridCells: GridCell[] = [];
  const gridRows: GridRow[] = [];
  const instrumentItems: InstrumentListItem[] = [];
  const orderChips: OrderChip[] = [];
  const patternOptionItems: PatternOptionItem[] = [];
  const channelMuteButtons: HTMLButtonElement[] = [];
  const channelSoloButtons: HTMLButtonElement[] = [];
  let visible = false;
  let lastAutoScrollKey = "";
  let leftPanelCollapsed = false;
  let lastProjectRenderSignature = "";
  let lastProjectRevision = store.getState().projectRevision;
  let lastTransportSignature = `${store.getState().ui.loopEnabled}:${store.getState().ui.muteMask}:${store.getState().ui.soloMask}`;
  let cachedDiagnosticsRevision = -1;
  let cachedDiagnostics: TrackerDiagnosticsResult | null = null;
  let lastRenderedPlayheadRow = -1;
  let lastRenderedPlayingOrder = -1;
  let lastRenderedTrackerOwned = false;
  let rightPanelCollapsed = false;
  let activePlaybackButton: ActivePlaybackButton = null;
  let canceledTransportToken: number | null = null;
  let transportRequestToken = 0;
  let transportStartPending = false;
  let draggingOrderIndex: number | null = null;
  let dropOrderIndex: number | null = null;
  let menuBar: WorkbenchMenuBarController | null = null;
  let patternPickerOpen = false;

  const refreshMenuBar = (): void => {
    menuBar?.refresh();
  };

  const getCachedDiagnostics = (state: ReturnType<TrackerStore["getState"]>): TrackerDiagnosticsResult => {
    if (cachedDiagnostics !== null && cachedDiagnosticsRevision === state.projectRevision) {
      return cachedDiagnostics;
    }
    let diagnostics: ReturnType<typeof analyzeTrackerProject> | null = null;
    let error: string | null = null;
    try {
      diagnostics = analyzeTrackerProject(state.project);
    } catch (analysisError) {
      error = analysisError instanceof Error ? analysisError.message : String(analysisError);
    }
    cachedDiagnosticsRevision = state.projectRevision;
    cachedDiagnostics = { diagnostics, error };
    return cachedDiagnostics;
  };

  const projectRenderSignature = (state: ReturnType<TrackerStore["getState"]>): string => [
    state.projectRevision,
    state.ui.activePattern,
    state.ui.selectedInstrument,
    state.ui.selectedOrderSlot,
    state.ui.selection.anchorChannel,
    state.ui.selection.anchorRow,
    state.ui.selection.focusChannel,
    state.ui.selection.focusRow,
    state.ui.currentOctave,
    state.ui.editStep,
    state.ui.followRow ? 1 : 0,
    state.ui.loopEnabled ? 1 : 0,
    state.ui.muteMask,
    state.ui.soloMask,
    state.undoDepth,
    state.redoDepth,
    state.lastUndoLabel ?? "",
    state.lastRedoLabel ?? "",
    state.canUndo ? 1 : 0,
    state.canRedo ? 1 : 0,
    patternPickerOpen ? 1 : 0,
    draggingOrderIndex ?? -1,
    dropOrderIndex ?? -1,
    visible ? 1 : 0,
    leftPanelCollapsed ? 1 : 0,
    rightPanelCollapsed ? 1 : 0
  ].join(":");

  const isTransportButton = (button: ActivePlaybackButton): button is ActiveTransportButton => button === "song" || button === "pattern" || button === "row";

  const trackerTransportPlaybackActive = (_state: ReturnType<TrackerStore["getState"]>): boolean =>
    isTransportButton(activePlaybackButton);

  const beginTransportStart = (button: ActiveTransportButton): number => {
    transportRequestToken += 1;
    canceledTransportToken = null;
    transportStartPending = true;
    activePlaybackButton = button;
    syncPlaybackButtons(store.getState());
    return transportRequestToken;
  };

  const syncPlaybackButtons = (state: ReturnType<TrackerStore["getState"]>): void => {
    const trackerPlaybackActive = state.ui.playback.state === "playing" || state.ui.playback.state === "written";
    if (isTransportButton(activePlaybackButton) && !trackerPlaybackActive && !transportStartPending) {
      activePlaybackButton = null;
    }
    if (activePlaybackButton === "instrument" && state.ui.playback.state !== "auditioning") {
      activePlaybackButton = null;
    }
    setText(playSong, activePlaybackButton === "song" && (trackerPlaybackActive || transportStartPending) ? "Stop" : "Play");
    setText(playPattern, activePlaybackButton === "pattern" && (trackerPlaybackActive || transportStartPending) ? "Stop" : "Play");
    setText(playFromRow, activePlaybackButton === "row" && (trackerPlaybackActive || transportStartPending) ? "Stop" : "Play from");
    setText(playInstrument, activePlaybackButton === "instrument" && state.ui.playback.state === "auditioning" ? "Stop" : "Play");
  };

  const stopTrackerPlayback = (): void => {
    if (transportStartPending && isTransportButton(activePlaybackButton)) {
      canceledTransportToken = transportRequestToken;
    }
    transportStartPending = false;
    playback.stop();
    playback.releaseAudition();
    activePlaybackButton = null;
    store.updatePlayback({
      lastError: null,
      lastMessage: "Stop requested through the tracker status block.",
      ownedByTracker: true,
      state: "stopped"
    });
  };

  const stopInstrumentPreview = (): void => {
    playback.releaseAudition();
    transportStartPending = false;
    activePlaybackButton = null;
    const state = store.getState();
    store.updatePlayback({
      lastError: null,
      lastMessage: "Stopped instrument preview.",
      ownedByTracker: false,
      state: state.ui.playback.workspaceWritten ? "written" : "idle"
    });
  };

  panel.addEventListener(
    "pointerdown",
    () => {
      host.startAudio();
    },
    { passive: true }
  );

  updateSelectOptions(
    cellNote,
    allNoteOptions(store.getState().project.song.globals.pitchMode).map((option) => ({ value: String(option.code), label: option.label }))
  );
  updateSelectOptions(
    cellEffect,
    TRACKER_EFFECT_OPTIONS.map((option) => ({
      value: String(option.code),
      label: option.code === TRACKER_EFFECT.none ? option.label : `${option.label} - ${effectScopeLabel(option.code)}`
    }))
  );
  updateSelectOptions(waveform, [
    { value: "triangle", label: "Triangle" },
    { value: "saw", label: "Saw" },
    { value: "pulse", label: "Pulse" },
    { value: "noise", label: "Noise" }
  ]);
  updateSelectOptions(globalPitch, [
    { value: "0", label: "COARSE_16" },
    { value: "1", label: "BALANCED_19" },
    { value: "2", label: "FINE_20" }
  ]);
  const adsrOptions = Array.from({ length: 16 }, (_, value) => ({ value: String(value), label: String(value) }));
  updateSelectOptions(attack, adsrOptions);
  updateSelectOptions(decay, adsrOptions);
  updateSelectOptions(sustain, adsrOptions);
  updateSelectOptions(release, adsrOptions);

  for (let channel = 0; channel < 4; channel += 1) {
    const muteButton = expectElement(panel, `tracker-mute-${channel}`, HTMLButtonElement);
    const soloButton = expectElement(panel, `tracker-solo-${channel}`, HTMLButtonElement);
    muteButton.addEventListener("click", () => {
      store.toggleMute(channel);
    });
    soloButton.addEventListener("click", () => {
      store.toggleSolo(channel);
    });
    channelMuteButtons.push(muteButton);
    channelSoloButtons.push(soloButton);
  }

  for (let row = 0; row < 64; row += 1) {
    const tableRow = document.createElement("tr");
    tableRow.className = "tracker-grid__row";
    tableRow.dataset.row = String(row);
    const rowLabel = document.createElement("th");
    rowLabel.className = "tracker-grid__row-label";
    rowLabel.scope = "row";
    rowLabel.textContent = row.toString().padStart(2, "0");
    gridRows.push({ element: tableRow, label: rowLabel, row });
    tableRow.append(rowLabel);
    for (let channel = 0; channel < 4; channel += 1) {
      const cell = document.createElement("td");
      const button = document.createElement("button");
      button.type = "button";
      button.className = "tracker-grid__cell";
      button.dataset.row = String(row);
      button.dataset.channel = String(channel);
      const color = document.createElement("span");
      color.className = "tracker-grid__cell-color";
      color.setAttribute("aria-hidden", "true");
      const note = document.createElement("span");
      note.className = "tracker-grid__cell-note";
      const instrument = document.createElement("span");
      instrument.className = "tracker-grid__cell-inst";
      const effect = document.createElement("span");
      effect.className = "tracker-grid__cell-fx";
      const effectArg = document.createElement("span");
      effectArg.className = "tracker-grid__cell-arg";
      button.append(color, note, instrument, effect, effectArg);
      button.addEventListener("click", () => {
        store.setSelection(row, channel);
        grid.focus();
      });
      gridCells.push({ button, channel, color, effect, effectArg, instrument, note, row });
      cell.append(button);
      tableRow.append(cell);
    }
    gridBody.append(tableRow);
  }

  const ensurePlayheadVisible = (rowIndex: number): void => {
    const row = gridRows[rowIndex];
    if (row === undefined) {
      return;
    }
    const headerCell = gridScroll.querySelector(".tracker-grid__table thead th");
    const rowRect = row.element.getBoundingClientRect();
    const wrapRect = gridScroll.getBoundingClientRect();
    const headerHeight = headerCell instanceof HTMLElement ? headerCell.getBoundingClientRect().height : 0;
    const visibleTop = wrapRect.top + headerHeight;
    const maxScrollTop = Math.max(0, gridScroll.scrollHeight - gridScroll.clientHeight);
    const targetScrollTop = Math.max(0, Math.min(maxScrollTop, row.element.offsetTop - headerHeight));
    if (rowRect.top < visibleTop) {
      gridScroll.scrollTop = targetScrollTop;
      return;
    }
    if (rowRect.bottom >= wrapRect.bottom - 1) {
      gridScroll.scrollTop = targetScrollTop;
    }
  };

  const withProjectError = async (action: () => Promise<void> | void): Promise<void> => {
    try {
      await action();
      const state = store.getState();
      store.updatePlayback({
        lastError: null,
        lastMessage: state.ui.playback.lastMessage
      });
    } catch (error) {
      const message = error instanceof Error ? error.message : String(error);
      store.updatePlayback({
        lastError: message,
        state: "error"
      });
      host.setActivityStatus(message);
    }
  };

  const updateInstrumentSelects = (project: TrackerProject): void => {
    updateSelectOptions(cellInstrument, [
      { value: String(TRACKER_INSTRUMENT_EMPTY), label: ".." },
      ...project.song.instruments.map((instrument, index) => ({
        value: String(index),
        label: `${(index + 1).toString().padStart(2, "0")} ${instrument.name}`
      }))
    ]);
    updateSelectOptions(
      activePattern,
      project.song.patterns.map((pattern, index) => ({ value: String(index), label: `${index.toString().padStart(2, "0")} ${pattern.name}` }))
    );
  };

  const ensurePatternOptions = (project: TrackerProject): void => {
    const signature = project.song.patterns.map((pattern, index) => `${index}:${pattern.name}`).join("|");
    if (patternNameOptions.dataset.signature === signature) {
      return;
    }
    patternNameOptions.textContent = "";
    patternOptionItems.length = 0;
    project.song.patterns.forEach((pattern, index) => {
      const button = document.createElement("button");
      button.type = "button";
      button.className = "tracker-pattern-selector__option";
      button.setAttribute("role", "option");
      button.addEventListener("click", () => {
        patternPickerOpen = false;
        store.setActivePattern(index);
        window.requestAnimationFrame(() => {
          patternName.focus();
          patternName.select();
        });
      });
      patternOptionItems.push({ button });
      patternNameOptions.append(button);
    });
    patternNameOptions.dataset.signature = signature;
  };

  const ensureOrderStrip = (project: TrackerProject): void => {
    const signature = project.song.orderList
      .map((patternIndex, orderIndex) => `${orderIndex}:${patternIndex}:${project.song.patterns[patternIndex]?.name ?? "Missing Pattern"}`)
      .join("|");
    if (orderStrip.dataset.signature === signature) {
      return;
    }
    orderStrip.textContent = "";
    orderChips.length = 0;
    project.song.orderList.forEach((patternIndex, orderIndex) => {
      const chip = document.createElement("button");
      chip.type = "button";
      chip.className = "tracker-order-chip";
      chip.draggable = true;
      chip.addEventListener("click", () => {
        store.setSelectedOrderSlot(orderIndex);
      });
      chip.addEventListener("dragstart", (event) => {
        draggingOrderIndex = orderIndex;
        dropOrderIndex = orderIndex;
        chip.classList.add("is-dragging");
        if (event.dataTransfer !== null) {
          event.dataTransfer.effectAllowed = "move";
          event.dataTransfer.setData("text/plain", String(orderIndex));
        }
      });
      chip.addEventListener("dragover", (event) => {
        if (draggingOrderIndex === null || draggingOrderIndex === orderIndex) {
          return;
        }
        event.preventDefault();
        if (dropOrderIndex !== orderIndex) {
          dropOrderIndex = orderIndex;
          render();
        }
      });
      chip.addEventListener("drop", (event) => {
        if (draggingOrderIndex === null) {
          return;
        }
        event.preventDefault();
        const fromIndex = draggingOrderIndex;
        draggingOrderIndex = null;
        dropOrderIndex = null;
        store.reorderOrderSlot(fromIndex, orderIndex);
      });
      chip.addEventListener("dragend", () => {
        draggingOrderIndex = null;
        dropOrderIndex = null;
        render();
      });

      const slot = document.createElement("span");
      slot.className = "tracker-order-chip__slot";
      const pattern = document.createElement("span");
      pattern.className = "tracker-order-chip__pattern";
      chip.append(slot, pattern);
      orderChips.push({ button: chip, pattern, slot });
      orderStrip.append(chip);
    });
    orderStrip.dataset.signature = signature;
  };

  const ensureInstrumentList = (project: TrackerProject): void => {
    const signature = project.song.instruments
      .map((instrument, index) => `${index}:${instrument.name}:${instrument.color}`)
      .join("|");
    if (instrumentList.dataset.signature === signature) {
      return;
    }
    instrumentList.textContent = "";
    instrumentItems.length = 0;
    project.song.instruments.forEach((instrument, index) => {
      const button = document.createElement("button");
      button.type = "button";
      button.className = "tracker-instrument-list__item";
      button.style.borderLeftColor = instrument.color;
      button.addEventListener("click", () => {
        store.setSelectedInstrument(index);
      });
      instrumentItems.push({ button });
      instrumentList.append(button);
    });
    instrumentList.dataset.signature = signature;
  };

  const previewNoteCode = (state: ReturnType<TrackerStore["getState"]>): number => {
    const pitchMode = state.project.song.globals.pitchMode;
    const selectedEvent = state.project.song.patterns[state.ui.activePattern].rows[state.ui.selection.focusRow][state.ui.selection.focusChannel];
    if (selectedEvent.note !== TRACKER_NOTE_EMPTY && !isTrackerControlNote(selectedEvent.note) && isNoteValidForPitchMode(selectedEvent.note, pitchMode)) {
      return selectedEvent.note;
    }
    return makeNoteCode(state.ui.currentOctave, 0, pitchMode) ?? 25;
  };

  const updatePanelRails = (): void => {
    panel.dataset.leftCollapsed = leftPanelCollapsed ? "true" : "false";
    panel.dataset.rightCollapsed = rightPanelCollapsed ? "true" : "false";
    leftRail.setAttribute("aria-expanded", leftPanelCollapsed ? "false" : "true");
    rightRail.setAttribute("aria-expanded", rightPanelCollapsed ? "false" : "true");
    leftRail.textContent = leftPanelCollapsed ? "SHOW" : "HIDE";
    rightRail.textContent = rightPanelCollapsed ? "SHOW" : "HIDE";
    leftRail.title = leftPanelCollapsed ? "Show Song, Composition, and Status" : "Hide Song, Composition, and Status";
    rightRail.title = rightPanelCollapsed ? "Show Instrument panel" : "Hide Instrument panel";
    leftRail.setAttribute("aria-label", leftPanelCollapsed ? "Show Song, Composition, and Status" : "Hide Song, Composition, and Status");
    rightRail.setAttribute("aria-label", rightPanelCollapsed ? "Show Instrument panel" : "Hide Instrument panel");
    trackerLayout.classList.toggle("is-left-collapsed", leftPanelCollapsed);
    trackerLayout.classList.toggle("is-right-collapsed", rightPanelCollapsed);
  };

  const buildMenus = (): WorkbenchMenu[] => [
    {
      id: "file",
      label: "File",
      items: [
        buttonMenuItem("new-song", newSong),
        buttonMenuItem("load-project", importJson),
        buttonMenuItem("save-project", exportJson),
        buttonMenuItem("export-bytecode", exportBytecode)
      ]
    },
    {
      id: "edit",
      label: "Edit",
      items: [
        buttonMenuItem("undo", undo, { shortcut: "Ctrl/Cmd+Z" }),
        buttonMenuItem("redo", redo, { shortcut: "Ctrl/Cmd+Shift+Z" }),
        menuSeparator(),
        buttonMenuItem("add-pattern", addPattern),
        buttonMenuItem("delete-pattern", deletePattern),
        buttonMenuItem("duplicate-pattern", duplicatePattern)
      ]
    },
    {
      id: "view",
      label: "View",
      items: [
        actionMenuItem(
          "toggle-left-panel",
          leftPanelCollapsed ? "Show Song / Composition / Status" : "Hide Song / Composition / Status",
          true,
          () => {
            leftRail.click();
          }
        ),
        actionMenuItem(
          "toggle-right-panel",
          rightPanelCollapsed ? "Show Instruments Panel" : "Hide Instruments Panel",
          true,
          () => {
            rightRail.click();
          }
        )
      ]
    },
    {
      id: "playback",
      label: "Playback",
      items: [
        buttonMenuItem("play-song", playSong),
        buttonMenuItem("play-pattern", playPattern),
        buttonMenuItem("play-from-row", playFromRow),
        menuSeparator(),
        checkboxMenuItem("loop", "Loop", loopToggle),
        checkboxMenuItem("follow-row", "Follow Row", followToggle)
      ]
    },
    {
      id: "pattern",
      label: "Pattern",
      items: [
        buttonMenuItem("copy", copy),
        buttonMenuItem("paste", paste),
        buttonMenuItem("clear", clear),
        menuSeparator(),
        buttonMenuItem("transpose-down", transposeDown),
        buttonMenuItem("transpose-up", transposeUp),
        buttonMenuItem("insert-row", insertRow),
        buttonMenuItem("delete-row", deleteRow),
        buttonMenuItem("duplicate-row", duplicateRow)
      ]
    },
    {
      id: "instrument",
      label: "Instrument",
      items: [
        buttonMenuItem("add-instrument", addInstrument),
        buttonMenuItem("delete-instrument", deleteInstrument),
        buttonMenuItem("duplicate-instrument", duplicateInstrument),
        buttonMenuItem("play-instrument", playInstrument)
      ]
    },
    {
      id: "emulator",
      label: "Emulator",
      items: [
        buttonMenuItem("write-emulator", writeEmulator),
        buttonMenuItem("start-emulator", startEmulator)
      ]
    }
  ];

  const renderProjectUi = (state: ReturnType<TrackerStore["getState"]>): void => {
    const { project } = state;
    const pitchMode = project.song.globals.pitchMode;
    const selectedInstrumentIndex = state.ui.selectedInstrument;
    const selectedInstrument = project.song.instruments[selectedInstrumentIndex];
    const bounds = trackerSelectionBounds(state.ui.selection);
    const selectedEvent = project.song.patterns[state.ui.activePattern].rows[state.ui.selection.focusRow][state.ui.selection.focusChannel];
    const { diagnostics, error: diagnosticsError } = getCachedDiagnostics(state);
    const invalidCellMessages = new Map<string, string>();
    if (diagnostics !== null) {
      diagnostics.noteErrors.forEach((noteError) => {
        invalidCellMessages.set(trackerCellKey(noteError.pattern, noteError.row, noteError.channel), noteError.message);
      });
    }

    updateInstrumentSelects(project);
    updateSelectOptions(
      cellNote,
      allNoteOptions(pitchMode, selectedEvent.note).map((option) => ({ value: String(option.code), label: option.label }))
    );
    setInputValue(songTitle, project.title);
    setInputValue(speed, String(project.song.speed));
    setInputValue(tempo, String(project.song.tempo));
    setInputValue(orderList, formatOrderList(project.song.orderList));
    setInputValue(activePattern, String(state.ui.activePattern));
    setInputValueUnlessFocused(patternName, project.song.patterns[state.ui.activePattern].name);
    patternName.setAttribute("aria-expanded", patternPickerOpen ? "true" : "false");
    patternNameToggle.setAttribute("aria-expanded", patternPickerOpen ? "true" : "false");
    patternNameOptions.hidden = !patternPickerOpen;
    loopToggle.checked = state.ui.loopEnabled;
    followToggle.checked = state.ui.followRow;
    setInputValue(editStep, String(state.ui.editStep));
    setInputValue(octave, String(state.ui.currentOctave));

    setInputValue(cellNote, String(selectedEvent.note));
    setInputValue(cellInstrument, String(selectedEvent.instrument));
    setInputValue(cellEffect, String(selectedEvent.effect));
    setInputValue(cellEffectArg, selectedEvent.effectArg.toString(16).toUpperCase().padStart(2, "0"));
    cellEffectArg.disabled = selectedEvent.effect === TRACKER_EFFECT.none;
    cellEffectArg.title = effectArgHint(selectedEvent.effect);

    setInputValue(instrumentName, selectedInstrument.name);
    setInputValue(waveform, selectedInstrument.waveform);
    sync.checked = selectedInstrument.sync;
    ring.checked = selectedInstrument.ring;
    test.checked = selectedInstrument.test;
    setInputValue(attack, String((selectedInstrument.ad >> 4) & 0x0f));
    setInputValue(decay, String(selectedInstrument.ad & 0x0f));
    setInputValue(sustain, String((selectedInstrument.sr >> 4) & 0x0f));
    setInputValue(release, String(selectedInstrument.sr & 0x0f));
    setInputValue(pulseWidth, String(selectedInstrument.pulseWidth));
    pulseWidthField.classList.toggle("is-hidden", selectedInstrument.waveform !== TRACKER_WAVEFORM.pulse);
    pulseWidthField.setAttribute("aria-hidden", selectedInstrument.waveform === TRACKER_WAVEFORM.pulse ? "false" : "true");
    pulseWidth.disabled = selectedInstrument.waveform !== TRACKER_WAVEFORM.pulse;
    rateX8.checked = selectedInstrument.auxRateX8;
    filterRoute.checked = selectedInstrument.filterRouteEnabled;

    setInputValue(globalPitch, String(project.song.globals.pitchMode));
    setText(pitchRangeHint, pitchModeRangeLabel(pitchMode));
    setInputValue(globalVolume, String(project.song.globals.masterVolume));
    setInputValue(globalCutoff, String(project.song.globals.filterCutoff));
    setInputValue(globalFilterRes, String(project.song.globals.filterCtrl & 0x0f));
    setInputValue(globalFilterMode, String(trackerFilterMode(project.song.globals.filterCtrl)));

    ensureOrderStrip(project);
    orderChips.forEach((chip, orderIndex) => {
      const patternIndex = project.song.orderList[orderIndex] ?? 0;
      const patternName = project.song.patterns[patternIndex]?.name ?? "Missing Pattern";
      setText(chip.slot, `Order ${orderIndex.toString().padStart(2, "0")}`);
      setText(chip.pattern, `Pattern ${patternIndex.toString().padStart(2, "0")}`);
      chip.button.classList.toggle("is-selected", orderIndex === state.ui.selectedOrderSlot);
      chip.button.classList.toggle("is-dragging", orderIndex === draggingOrderIndex);
      chip.button.classList.toggle("is-drop-target", draggingOrderIndex !== null && orderIndex === dropOrderIndex && orderIndex !== draggingOrderIndex);
      chip.button.title = `${chip.slot.textContent} -> ${patternName}. Drag to reorder.`;
    });

    ensurePatternOptions(project);
    patternOptionItems.forEach((item, index) => {
      const optionName = project.song.patterns[index]?.name ?? `Pattern ${index.toString().padStart(2, "0")}`;
      setText(item.button, `${index.toString().padStart(2, "0")} ${optionName}`);
      item.button.classList.toggle("is-active", index === state.ui.activePattern);
      item.button.setAttribute("aria-selected", index === state.ui.activePattern ? "true" : "false");
    });

    ensureInstrumentList(project);
    project.song.instruments.forEach((instrument, index) => {
      const item = instrumentItems[index];
      if (item === undefined) {
        return;
      }
      item.button.classList.toggle("is-active", index === selectedInstrumentIndex);
      setText(item.button, `${(index + 1).toString().padStart(2, "0")} ${instrument.name}`);
      item.button.style.borderLeftColor = instrument.color;
      item.button.setAttribute("aria-pressed", index === selectedInstrumentIndex ? "true" : "false");
    });

    for (const cell of gridCells) {
      const event = project.song.patterns[state.ui.activePattern].rows[cell.row][cell.channel];
      const parts = formatCellParts(event);
      const invalidMessage = invalidCellMessages.get(trackerCellKey(state.ui.activePattern, cell.row, cell.channel));
      setText(cell.note, parts.note);
      setText(cell.instrument, parts.instrument);
      setText(cell.effect, parts.effect);
      setText(cell.effectArg, parts.effectArg);
      cell.button.setAttribute(
        "aria-label",
        `Row ${cell.row.toString().padStart(2, "0")}, V${cell.channel + 1}, ${formatCellAriaLabel(event)}${invalidMessage === undefined ? "" : `. ${invalidMessage}.`}`
      );
      const instrumentColor = event.instrument === TRACKER_INSTRUMENT_EMPTY ? "transparent" : (project.song.instruments[event.instrument]?.color ?? "transparent");
      cell.color.style.backgroundColor = instrumentColor;
      cell.button.classList.toggle(
        "is-selected",
        cell.row >= bounds.startRow &&
          cell.row <= bounds.endRow &&
          cell.channel >= bounds.startChannel &&
          cell.channel <= bounds.endChannel
      );
      cell.button.classList.toggle("is-muted", channelEffectivelyMuted(state, cell.channel));
      cell.button.classList.toggle("is-invalid", invalidMessage !== undefined);
      cell.button.setAttribute("aria-invalid", invalidMessage === undefined ? "false" : "true");
      cell.button.title = invalidMessage ?? "";
    }

    channelMuteButtons.forEach((button, index) => {
      const active = (state.ui.muteMask & (1 << index)) !== 0;
      button.classList.toggle("is-active", active);
      button.setAttribute("aria-pressed", active ? "true" : "false");
    });
    channelSoloButtons.forEach((button, index) => {
      const active = (state.ui.soloMask & (1 << index)) !== 0;
      button.classList.toggle("is-active", active);
      button.setAttribute("aria-pressed", active ? "true" : "false");
    });

    setText(effectCode, effectName(selectedEvent.effect));
    setText(effectScope, effectScopeLabel(selectedEvent.effect));
    effectScope.dataset.scope = effectScopeLabel(selectedEvent.effect).toLowerCase().replace(/\s+/g, "-");
    setText(effectDescription, effectHelp(selectedEvent.effect));
    setText(effectArgHelp, effectArgHint(selectedEvent.effect));

    selectionStatus.textContent = `Pattern ${state.ui.activePattern.toString().padStart(2, "0")} | Row ${state.ui.selection.focusRow
      .toString()
      .padStart(2, "0")} | V${state.ui.selection.focusChannel + 1} | ${selectedCellSummary(selectedEvent)} | ${effectHelp(selectedEvent.effect)}`;
    ownershipStatus.textContent = state.ui.playback.ownedByTracker
      ? "Tracker owns the main emulator for playback or audition."
      : "Tracker is editing locally and has not reserved the main emulator.";

    historyStatus.textContent = `Undo ${state.undoDepth}${state.lastUndoLabel !== null ? ` (${state.lastUndoLabel})` : ""} | Redo ${state.redoDepth}${state.lastRedoLabel !== null ? ` (${state.lastRedoLabel})` : ""}`;
    undo.title = state.lastUndoLabel === null ? "Undo" : `Undo ${state.lastUndoLabel}`;
    redo.title = state.lastRedoLabel === null ? "Redo" : `Redo ${state.lastRedoLabel}`;
    deletePattern.disabled = project.song.patterns.length <= 1;
    deleteInstrument.disabled = project.song.instruments.length <= 1;

    diagnosticsList.textContent = "";
    diagnosticsIssues.textContent = "";
    if (diagnostics !== null) {
      diagnostics.metrics.forEach((metric) => {
        const chip = document.createElement("div");
        chip.className = "tracker-diagnostic";
        chip.dataset.tone = metricTone(metric.severity);
        const label = document.createElement("span");
        label.className = "tracker-diagnostic__label";
        label.textContent = metric.label;
        const value = document.createElement("span");
        value.className = "tracker-diagnostic__value";
        value.textContent = `${metric.used} / ${metric.max}`;
        chip.append(label, value);
        diagnosticsList.append(chip);
      });
      if (diagnostics.noteErrors.length > 0) {
        diagnostics.noteErrors.forEach((noteError) => {
          const issue = document.createElement("div");
          issue.className = "tracker-diagnostic-issue";
          issue.dataset.tone = "error";
          issue.textContent = noteError.message;
          diagnosticsIssues.append(issue);
        });
        diagnosticsIssues.hidden = false;
        diagnosticsSummary.dataset.tone = "error";
        diagnosticsSummary.textContent =
          diagnostics.noteErrors.length === 1
            ? `${diagnostics.noteErrors[0].message}. Playback, write, and export stay blocked until the note is fixed or the pitch mode changes.`
            : `${diagnostics.noteErrors.length} notes are outside the ${pitchModeDisplayName(pitchMode)} range ${pitchModeRangeLabel(pitchMode)}. Playback, write, and export stay blocked until every location is fixed or the pitch mode changes.`;
      } else {
        diagnosticsIssues.hidden = true;
        diagnosticsSummary.dataset.tone = metricTone(diagnostics.status);
        diagnosticsSummary.textContent =
          diagnostics.status === "error"
            ? "Compile limits are exceeded. Playback and export will fail until the project fits the v1 caps."
            : diagnostics.status === "warn"
              ? "The current project is close to one or more v1 compile limits."
              : "Tracker stays within the v1 compile limits.";
      }
    } else {
      diagnosticsIssues.hidden = true;
      diagnosticsSummary.dataset.tone = "error";
      diagnosticsSummary.textContent = diagnosticsError ?? "Tracker diagnostics are unavailable.";
    }

    undo.disabled = !state.canUndo;
    redo.disabled = !state.canRedo;
    updatePanelRails();
    refreshMenuBar();
  };

  const renderPlaybackUi = (state: ReturnType<TrackerStore["getState"]>, force = false): void => {
    const { project } = state;
    const selectedInstrument = project.song.instruments[state.ui.selectedInstrument];
    const { diagnostics } = getCachedDiagnostics(state);
    const playheadVisible = state.ui.playback.pattern === state.ui.activePattern ? state.ui.playback.row : -1;
    const pitchBlocked = diagnostics !== null && diagnostics.noteErrors.length > 0;
    const songTransportActive = activePlaybackButton === "song" && (state.ui.playback.state === "playing" || state.ui.playback.state === "written" || transportStartPending);
    const patternTransportActive = activePlaybackButton === "pattern" && (state.ui.playback.state === "playing" || state.ui.playback.state === "written" || transportStartPending);
    const rowTransportActive = activePlaybackButton === "row" && (state.ui.playback.state === "playing" || state.ui.playback.state === "written" || transportStartPending);

    if (force || lastRenderedPlayheadRow !== playheadVisible) {
      const previousRow = gridRows[lastRenderedPlayheadRow];
      if (previousRow !== undefined) {
        previousRow.element.classList.remove("is-playhead");
        previousRow.label.setAttribute("aria-current", "false");
        for (const cell of gridCells) {
          if (cell.row === lastRenderedPlayheadRow) {
            cell.button.classList.remove("is-playhead");
          }
        }
      }
      const nextRow = gridRows[playheadVisible];
      if (nextRow !== undefined) {
        nextRow.element.classList.add("is-playhead");
        nextRow.label.setAttribute("aria-current", "true");
        for (const cell of gridCells) {
          if (cell.row === playheadVisible) {
            cell.button.classList.add("is-playhead");
          }
        }
      }
      lastRenderedPlayheadRow = playheadVisible;
    }

    const playingOrder = state.ui.playback.ownedByTracker ? state.ui.playback.order : -1;
    if (force || lastRenderedPlayingOrder !== playingOrder || lastRenderedTrackerOwned !== state.ui.playback.ownedByTracker) {
      const previousChip = orderChips[lastRenderedPlayingOrder];
      if (previousChip !== undefined) {
        previousChip.button.classList.remove("is-playing");
        previousChip.button.setAttribute("aria-current", "false");
      }
      const nextChip = orderChips[playingOrder];
      if (nextChip !== undefined) {
        nextChip.button.classList.add("is-playing");
        nextChip.button.setAttribute("aria-current", "true");
      }
      lastRenderedPlayingOrder = playingOrder;
      lastRenderedTrackerOwned = state.ui.playback.ownedByTracker;
    }

    ownershipStatus.textContent = state.ui.playback.ownedByTracker
      ? "Tracker owns the main emulator for playback or audition."
      : "Tracker is editing locally and has not reserved the main emulator.";
    audioDiagnostics.textContent = state.ui.playback.audioDiagnostics;
    audioStatus.textContent = state.ui.playback.audioStatus;
    playbackStatus.textContent = `${playbackStateText(state.ui.playback.state)} Order ${state.ui.playback.order
      .toString()
      .padStart(2, "0")} | Row ${state.ui.playback.row.toString().padStart(2, "0")} | Tick ${state.ui.playback.tick}`;
    errorStatus.textContent = state.ui.playback.lastError ?? state.ui.playback.lastMessage;
    ownershipStatus.dataset.tone = state.ui.playback.ownedByTracker ? "warn" : "ok";
    playbackStatus.dataset.tone = state.ui.playback.state === "error" ? "error" : state.ui.playback.state === "playing" ? "warn" : "ok";
    errorStatus.dataset.tone = latestResultTone(state);

    syncPlaybackButtons(state);
    playInstrument.title = activePlaybackButton === "instrument" && state.ui.playback.state === "auditioning"
      ? "Stop instrument preview."
      : `Preview ${selectedInstrument.name} on ${noteCodeToLabel(previewNoteCode(state))}.`;
    playSong.disabled = pitchBlocked && !songTransportActive;
    playPattern.disabled = pitchBlocked && !patternTransportActive;
    playFromRow.disabled = pitchBlocked && !rowTransportActive;
    exportBytecode.disabled = pitchBlocked;
    writeEmulator.disabled = pitchBlocked;
    startEmulator.disabled = !state.ui.playback.workspaceWritten || pitchBlocked;

    const autoScrollKey = visible && state.ui.followRow && state.ui.playback.state === "playing" && playheadVisible >= 0
      ? `${state.ui.playback.pattern}:${state.ui.playback.row}`
      : "";
    if (autoScrollKey !== "" && autoScrollKey !== lastAutoScrollKey) {
      ensurePlayheadVisible(playheadVisible);
    }
    lastAutoScrollKey = autoScrollKey;
    refreshMenuBar();
  };

  const render = (forceProject = false): void => {
    const state = store.getState();
    const signature = projectRenderSignature(state);
    let projectRendered = false;
    if (forceProject || signature !== lastProjectRenderSignature) {
      renderProjectUi(state);
      lastProjectRenderSignature = signature;
      projectRendered = true;
    }
    renderPlaybackUi(state, projectRendered);
  };

  store.subscribe((state) => {
    if (state.projectRevision !== lastProjectRevision) {
      lastProjectRevision = state.projectRevision;
      saveDraftProject(state.project);
      if (state.ui.playback.workspaceWritten) {
        playback.queueProjectRefresh();
      }
    }
    const transportSignature = `${state.ui.loopEnabled}:${state.ui.muteMask}:${state.ui.soloMask}`;
    if (transportSignature !== lastTransportSignature) {
      lastTransportSignature = transportSignature;
      if (state.ui.playback.workspaceWritten) {
        playback.syncTransport(state.ui.loopEnabled, state.ui.muteMask, state.ui.soloMask);
      }
    }
    render();
  });

  const bindNumberInput = (input: HTMLInputElement | HTMLSelectElement, onValue: (value: number) => void): void => {
    const sync = () => {
      if (input.value.trim() === "") {
        return;
      }
      const value = Number(input.value);
      if (!Number.isFinite(value)) {
        return;
      }
      onValue(value);
    };
    input.addEventListener("input", sync);
    input.addEventListener("change", sync);
  };

  const bindTextInput = (input: HTMLInputElement, onValue: (value: string) => void): void => {
    const sync = () => {
      onValue(input.value);
    };
    input.addEventListener("input", sync);
    input.addEventListener("change", sync);
  };

  bindTextInput(songTitle, (value) => {
    store.updateTitle(value);
  });
  bindNumberInput(speed, (value) => {
    store.updateSpeed(value);
  });
  bindNumberInput(tempo, (value) => {
    store.updateTempo(value);
  });
  bindTextInput(patternName, (value) => {
    patternPickerOpen = false;
    store.updatePatternName(value);
  });
  const syncOrderList = () => {
    const state = store.getState();
    const nextOrderList = parseOrderList(orderList.value, state.project.song.patterns.length);
    if (formatOrderList(nextOrderList) === formatOrderList(state.project.song.orderList)) {
      return;
    }
    store.updateOrderList(nextOrderList);
  };
  orderList.addEventListener("input", syncOrderList);
  orderList.addEventListener("change", syncOrderList);
  leftRail.addEventListener("click", () => {
    leftPanelCollapsed = !leftPanelCollapsed;
    updatePanelRails();
  });
  rightRail.addEventListener("click", () => {
    rightPanelCollapsed = !rightPanelCollapsed;
    updatePanelRails();
  });
  newSong.addEventListener("click", () => {
    const state = store.getState();
    if (state.ui.playback.state === "playing") {
      playback.stop();
    }
    playback.releaseAudition();
    transportStartPending = false;
    activePlaybackButton = null;
    store.resetProject();
    store.updatePlayback({
      lastError: null,
      lastMessage: "Started a new tracker song.",
      ownedByTracker: false,
      state: "idle",
      workspaceWritten: false
    });
  });
  activePattern.addEventListener("change", () => {
    patternPickerOpen = false;
    store.setActivePattern(Number(activePattern.value));
  });
  patternNameToggle.addEventListener("click", () => {
    patternPickerOpen = !patternPickerOpen;
    render();
  });
  patternName.addEventListener("keydown", (event) => {
    if (event.key === "ArrowDown") {
      patternPickerOpen = true;
      render();
      event.preventDefault();
      return;
    }
    if (event.key === "Escape" && patternPickerOpen) {
      patternPickerOpen = false;
      render();
      event.preventDefault();
    }
  });
  document.addEventListener("pointerdown", (event) => {
    if (!patternPickerOpen) {
      return;
    }
    if (event.target instanceof Node && patternSelector.contains(event.target)) {
      return;
    }
    patternPickerOpen = false;
    render();
  });
  addPattern.addEventListener("click", () => {
    store.addPattern();
  });
  deletePattern.addEventListener("click", () => {
    store.deleteActivePattern();
  });
  duplicatePattern.addEventListener("click", () => {
    store.duplicateActivePattern();
  });

  loopToggle.addEventListener("change", () => {
    store.setLoopEnabled(loopToggle.checked);
  });
  followToggle.addEventListener("change", () => {
    store.setFollowRow(followToggle.checked);
  });
  bindNumberInput(editStep, (value) => {
    store.setEditStep(value);
  });
  bindNumberInput(octave, (value) => {
    store.setCurrentOctave(clampKeyboardOctave(value));
  });

  cellNote.addEventListener("change", () => {
    store.updateCurrentEvent({ note: Number(cellNote.value) });
  });
  cellInstrument.addEventListener("change", () => {
    store.updateCurrentEvent({ instrument: Number(cellInstrument.value) });
  });
  cellEffect.addEventListener("change", () => {
    store.updateCurrentEvent({ effect: Number(cellEffect.value) as (typeof TRACKER_EFFECT)[keyof typeof TRACKER_EFFECT] });
  });
  cellEffectArg.addEventListener("change", () => {
    store.updateCurrentEvent({ effectArg: sanitizeEffectArg(Number.parseInt(cellEffectArg.value || "0", 16)) });
  });

  copy.addEventListener("click", () => {
    store.copySelection();
  });
  paste.addEventListener("click", () => {
    store.pasteSelection();
  });
  clear.addEventListener("click", () => {
    store.clearSelection();
  });
  transposeUp.addEventListener("click", () => {
    store.transposeSelection(1);
  });
  transposeDown.addEventListener("click", () => {
    store.transposeSelection(-1);
  });
  insertRow.addEventListener("click", () => {
    store.insertRow();
  });
  deleteRow.addEventListener("click", () => {
    store.deleteRow();
  });
  duplicateRow.addEventListener("click", () => {
    store.duplicateRow();
  });
  undo.addEventListener("click", () => {
    store.undo();
  });
  redo.addEventListener("click", () => {
    store.redo();
  });

  bindTextInput(instrumentName, (value) => {
    store.renameSelectedInstrument(value);
  });
  addInstrument.addEventListener("click", () => {
    store.addInstrument();
    window.requestAnimationFrame(() => {
      instrumentName.focus();
      instrumentName.select();
    });
  });
  deleteInstrument.addEventListener("click", () => {
    playback.releaseAudition();
    transportStartPending = false;
    activePlaybackButton = null;
    store.deleteSelectedInstrument();
    store.updatePlayback({
      lastError: null,
      lastMessage: "Deleted the selected instrument.",
      ownedByTracker: false,
      state: store.getState().ui.playback.workspaceWritten ? "written" : "idle"
    });
  });
  duplicateInstrument.addEventListener("click", () => {
    store.duplicateSelectedInstrument();
  });
  playInstrument.addEventListener("click", () => {
    const currentState = store.getState();
    const trackerTransportActive = activePlaybackButton === "song" || activePlaybackButton === "pattern" || activePlaybackButton === "row";
    if (currentState.ui.playback.state === "playing" || trackerTransportActive) {
      store.updatePlayback({
        lastError: null,
        lastMessage: "Stop tracker playback before previewing an instrument."
      });
      return;
    }
    if (activePlaybackButton === "instrument" && currentState.ui.playback.state === "auditioning") {
      stopInstrumentPreview();
      return;
    }
    const state = store.getState();
    const note = previewNoteCode(state);
    playback.auditionNote(state.project, state.ui.selectedInstrument, note);
    activePlaybackButton = "instrument";
    store.updatePlayback({
      lastError: null,
      lastMessage: `Previewing ${state.project.song.instruments[state.ui.selectedInstrument]?.name ?? "instrument"} on ${noteCodeToLabel(note)}.`,
      ownedByTracker: true,
      state: "auditioning"
    });
  });
  waveform.addEventListener("change", () => {
    store.updateSelectedInstrument({ waveform: waveform.value as never });
  });
  sync.addEventListener("change", () => {
    store.updateSelectedInstrument({ sync: sync.checked });
  });
  ring.addEventListener("change", () => {
    store.updateSelectedInstrument({ ring: ring.checked });
  });
  test.addEventListener("change", () => {
    store.updateSelectedInstrument({ test: test.checked });
  });
  const syncAd = () => {
    store.updateSelectedInstrument({ ad: ((Number(attack.value) & 0x0f) << 4) | (Number(decay.value) & 0x0f) });
  };
  attack.addEventListener("change", syncAd);
  decay.addEventListener("change", syncAd);
  const syncSr = () => {
    store.updateSelectedInstrument({ sr: ((Number(sustain.value) & 0x0f) << 4) | (Number(release.value) & 0x0f) });
  };
  sustain.addEventListener("change", syncSr);
  release.addEventListener("change", syncSr);
  bindNumberInput(pulseWidth, (value) => {
    store.updateSelectedInstrument({ pulseWidth: value });
  });
  rateX8.addEventListener("change", () => {
    store.updateSelectedInstrument({ auxRateX8: rateX8.checked });
  });
  filterRoute.addEventListener("change", () => {
    store.updateSelectedInstrument({ filterRouteEnabled: filterRoute.checked });
  });

  const syncGlobalPitch = () => {
    store.updateGlobals({ pitchMode: Number(globalPitch.value) as (typeof TRACKER_PITCH_MODE)[keyof typeof TRACKER_PITCH_MODE] });
  };
  globalPitch.addEventListener("input", syncGlobalPitch);
  globalPitch.addEventListener("change", syncGlobalPitch);
  bindNumberInput(globalVolume, (value) => {
    store.updateGlobals({ masterVolume: value });
  });
  bindNumberInput(globalCutoff, (value) => {
    store.updateGlobals({ filterCutoff: value });
  });
  const syncGlobalFilter = () => {
    store.updateGlobals({
      filterCtrl: composeTrackerFilterCtrl(Number(globalFilterRes.value), Number(globalFilterMode.value))
    });
  };
  globalFilterRes.addEventListener("input", syncGlobalFilter);
  globalFilterRes.addEventListener("change", syncGlobalFilter);
  globalFilterMode.addEventListener("input", syncGlobalFilter);
  globalFilterMode.addEventListener("change", syncGlobalFilter);

  playSong.addEventListener("click", () => {
    const currentState = store.getState();
    if (activePlaybackButton === "song" && (currentState.ui.playback.state === "playing" || currentState.ui.playback.state === "written" || transportStartPending)) {
      stopTrackerPlayback();
      return;
    }
    const requestToken = beginTransportStart("song");
    void withProjectError(async () => {
      try {
        const state = store.getState();
        await playback.playSong(state.project, state.ui.loopEnabled, state.ui.muteMask, state.ui.soloMask);
        if (requestToken === canceledTransportToken) {
          playback.stop();
          return;
        }
        if (requestToken !== transportRequestToken) {
          return;
        }
        transportStartPending = false;
        activePlaybackButton = "song";
        store.updatePlayback({
          lastError: null,
          lastMessage: "Playing song in the main emulator.",
          ownedByTracker: true,
          state: "playing",
          workspaceWritten: true
        });
      } catch (error) {
        if (requestToken === transportRequestToken) {
          transportStartPending = false;
          activePlaybackButton = null;
        }
        throw error;
      }
    });
  });
  playPattern.addEventListener("click", () => {
    const currentState = store.getState();
    if (activePlaybackButton === "pattern" && (currentState.ui.playback.state === "playing" || currentState.ui.playback.state === "written" || transportStartPending)) {
      stopTrackerPlayback();
      return;
    }
    const requestToken = beginTransportStart("pattern");
    void withProjectError(async () => {
      try {
        const state = store.getState();
        await playback.playPattern(state.project, state.ui.activePattern, 0, state.ui.loopEnabled, state.ui.muteMask, state.ui.soloMask);
        if (requestToken === canceledTransportToken) {
          playback.stop();
          return;
        }
        if (requestToken !== transportRequestToken) {
          return;
        }
        transportStartPending = false;
        activePlaybackButton = "pattern";
        store.updatePlayback({
          lastError: null,
          lastMessage: "Playing the current pattern preview in the main emulator.",
          ownedByTracker: true,
          state: "playing",
          workspaceWritten: true
        });
      } catch (error) {
        if (requestToken === transportRequestToken) {
          transportStartPending = false;
          activePlaybackButton = null;
        }
        throw error;
      }
    });
  });
  playFromRow.addEventListener("click", () => {
    const currentState = store.getState();
    if (activePlaybackButton === "row" && (currentState.ui.playback.state === "playing" || currentState.ui.playback.state === "written" || transportStartPending)) {
      stopTrackerPlayback();
      return;
    }
    const requestToken = beginTransportStart("row");
    void withProjectError(async () => {
      try {
        const state = store.getState();
        await playback.playPattern(
          state.project,
          state.ui.activePattern,
          state.ui.selection.focusRow,
          state.ui.loopEnabled,
          state.ui.muteMask,
          state.ui.soloMask
        );
        if (requestToken === canceledTransportToken) {
          playback.stop();
          return;
        }
        if (requestToken !== transportRequestToken) {
          return;
        }
        transportStartPending = false;
        activePlaybackButton = "row";
        store.updatePlayback({
          lastError: null,
          lastMessage: `Playing from row ${state.ui.selection.focusRow.toString().padStart(2, "0")}.`,
          ownedByTracker: true,
          state: "playing",
          workspaceWritten: true
        });
      } catch (error) {
        if (requestToken === transportRequestToken) {
          transportStartPending = false;
          activePlaybackButton = null;
        }
        throw error;
      }
    });
  });

  exportJson.addEventListener("click", () => {
    void withProjectError(() => {
      downloadProjectJson(store.getState().project);
      store.updatePlayback({ lastMessage: "Exported tracker JSON project." });
    });
  });
  exportBytecode.addEventListener("click", () => {
    void withProjectError(() => {
      const project = store.getState().project;
      const compiled = compileTrackerProject(project);
      downloadTrackerBytecode(project, compiled.bytecode);
      store.updatePlayback({ lastMessage: `Exported ${compiled.bytecode.byteLength} tracker bytecode bytes.` });
    });
  });
  importJson.addEventListener("click", () => {
    importJsonFile.value = "";
    importJsonFile.click();
  });
  importJsonFile.addEventListener("change", () => {
    const file = importJsonFile.files?.item(0);
    if (file === null || file === undefined) {
      return;
    }
    void withProjectError(async () => {
      const project = await readProjectFile(file);
      store.setProject(project);
      store.updatePlayback({ lastMessage: `Imported tracker JSON project ${file.name}.` });
    });
  });
  writeEmulator.addEventListener("click", () => {
    void withProjectError(async () => {
      const state = store.getState();
      await playback.writeSong(state.project, state.ui.loopEnabled, state.ui.muteMask, state.ui.soloMask);
      transportStartPending = false;
      activePlaybackButton = null;
      store.updatePlayback({
        lastMessage: "Wrote tracker player and song data into emulator RAM without starting it.",
        ownedByTracker: true,
        state: "written",
        workspaceWritten: true
      });
    });
  });
  startEmulator.addEventListener("click", () => {
    void withProjectError(async () => {
      await playback.startWrittenSong();
      transportStartPending = false;
      activePlaybackButton = null;
      store.updatePlayback({
        lastMessage: "Started the already-written tracker workspace in the main emulator.",
        ownedByTracker: true,
        state: "playing"
      });
    });
  });

  grid.addEventListener("keydown", (event) => {
    if (event.target instanceof HTMLInputElement || event.target instanceof HTMLTextAreaElement || event.target instanceof HTMLSelectElement) {
      return;
    }
    const state = store.getState();
    const handled = handleTrackerKeyDown(event, {
      changeOctave(delta) {
        store.setCurrentOctave(clampKeyboardOctave(state.ui.currentOctave + delta));
      },
      clearSelection() {
        store.clearSelection();
      },
      copySelection() {
        store.copySelection();
      },
      moveSelection(deltaRow, deltaChannel, extend) {
        store.moveSelection(deltaRow, deltaChannel, extend);
      },
      noteDown(note) {
        store.applyNoteInput(note);
        const latest = store.getState();
        if (trackerTransportPlaybackActive(latest)) {
          store.updatePlayback({
            lastMessage: `Entered ${noteCodeToLabel(note)} at row ${state.ui.selection.focusRow.toString().padStart(2, "0")}.`
          });
          return;
        }
        playback.auditionNote(latest.project, latest.ui.selectedInstrument, note);
        store.updatePlayback({
          lastMessage: `Entered ${noteCodeToLabel(note)} at row ${state.ui.selection.focusRow.toString().padStart(2, "0")}.`,
          ownedByTracker: true,
          state: "auditioning"
        });
      },
      noteUp() {
        if (trackerTransportPlaybackActive(store.getState())) {
          return;
        }
        playback.releaseAudition();
        store.updatePlayback({
          lastMessage: "Released tracker audition note.",
          state: store.getState().ui.playback.workspaceWritten ? "written" : "idle"
        });
      },
      octave: state.ui.currentOctave,
      pitchMode: state.project.song.globals.pitchMode,
      pasteSelection() {
        store.pasteSelection();
      },
      redo() {
        store.redo();
      },
      selectionChannel: state.ui.selection.focusChannel,
      selectionRow: state.ui.selection.focusRow,
      setSelection(row, channel, extend) {
        store.setSelection(row, channel, extend);
      },
      transposeSelection(delta) {
        store.transposeSelection(delta);
      },
      undo() {
        store.undo();
      }
    });
    if (handled) {
      event.preventDefault();
    }
  });

  grid.addEventListener("keyup", (event) => {
    const state = store.getState();
    const handled = handleTrackerKeyUp(event, {
      changeOctave() {},
      clearSelection() {},
      copySelection() {},
      moveSelection() {},
      noteDown() {},
      noteUp() {
        if (trackerTransportPlaybackActive(store.getState())) {
          return;
        }
        playback.releaseAudition();
        store.updatePlayback({
          lastMessage: "Tracker audition released.",
          state: state.ui.playback.workspaceWritten ? "written" : "idle"
        });
      },
      octave: state.ui.currentOctave,
      pitchMode: state.project.song.globals.pitchMode,
      pasteSelection() {},
      redo() {},
      selectionChannel: state.ui.selection.focusChannel,
      selectionRow: state.ui.selection.focusRow,
      setSelection() {},
      transposeSelection() {},
      undo() {}
    });
    if (handled) {
      event.preventDefault();
    }
  });

  menuBar = createWorkbenchMenuBar(menuHost, {
    getMenus: buildMenus,
    label: "Tracker menu bar"
  });

  render();

  return {
    refresh() {
      render();
    },
    setVisible(nextVisible) {
      visible = nextVisible;
      if (!visible) {
        playback.releaseAudition();
      }
    },
    syncFromEmulator() {
      const currentState = store.getState();
      if (!visible && currentState.ui.playback.workspaceWritten === false) {
        return;
      }
      const status = playback.readStatus();
      const pendingRefresh = playback.flushQueuedProjectRefresh(currentState.project, status);
      if (pendingRefresh !== null) {
        void pendingRefresh.catch((error) => {
          const message = error instanceof Error ? error.message : String(error);
          store.updatePlayback({
            lastError: message,
            state: "error"
          });
          host.setActivityStatus(message);
        });
      }
      if (currentState.ui.playback.state === "auditioning" && activePlaybackButton === "instrument") {
        store.updatePlayback({
          audioDiagnostics: status.audioDiagnostics,
          audioStatus: status.audioStatus,
          ownedByTracker: true,
          state: "auditioning",
          workspaceWritten: currentState.ui.playback.workspaceWritten || status.workspaceWritten
        });
        return;
      }
      if (!status.valid && currentState.ui.playback.state === "auditioning") {
        store.updatePlayback({
          audioDiagnostics: status.audioDiagnostics,
          audioStatus: status.audioStatus,
          ownedByTracker: true,
          state: "auditioning"
        });
        return;
      }
      if (!status.valid && currentState.ui.playback.workspaceWritten === false) {
        store.updatePlayback({
          audioDiagnostics: status.audioDiagnostics,
          audioStatus: status.audioStatus,
          lastError: null,
          lastMessage: playbackStateText("idle"),
          ownedByTracker: false,
          state: "idle",
          workspaceWritten: false
        });
        return;
      }
      const stateName =
        status.state === TRACKER_STATUS_STATE.playing
          ? "playing"
          : status.state === TRACKER_STATUS_STATE.written
            ? "written"
            : status.state === TRACKER_STATUS_STATE.stopped
              ? "stopped"
              : status.state === TRACKER_STATUS_STATE.error
                ? "error"
                : "idle";
      store.updatePlayback({
        audioDiagnostics: status.audioDiagnostics,
        audioStatus: status.audioStatus,
        bytecodeSize: status.bytecodeSize,
        errorCode: status.errorCode,
        lastError: status.errorCode === 0 ? null : `Tracker player error ${status.errorCode}`,
        lastMessage: playbackStateText(stateName),
        order: status.currentOrder,
        ownedByTracker: status.ownedByTracker,
        pattern: status.currentPattern,
        row: status.currentRow,
        state: stateName,
        tick: status.currentTick,
        workspaceWritten: status.workspaceWritten
      });
    }
  };
}
